"use client";

import { Box, Text, Columns, Row } from "@dxp-feed/ui";
import ImageWrapper from "@/components/ImageWrapper";
import { IDepartment } from "@/utils/types/storeLocator";
import { changeTimeFormat } from "@/helpers/storeLocatorHelper";
import parse from "html-react-parser";
import { RenderHTML } from "@/helpers/ServerFuncytions";

interface DepartmentsToggleProps {
  key: string;
  selectedDepartment: IDepartment;
  closeDepartment: () => void;
}

export const sanitizeHtmlString = (html?: string | null): string => {
  if (typeof html !== "string" || !html.trim()) {
    return "";
  }

  return html
    .replace(/<\/br>/gi, "<br />") // Replace invalid </br> with <br />
    .replace(/<br>/gi, "<br />") // Normalize <br> to <br />
    .replace(/\r\n|\n|\r/g, ""); // Remove newline artifacts
};

//to check the value of the proprietor (hide the column)
function hasValidProprietor(proprietor: any) {
  if (!proprietor) return false;

  const values = [
    proprietor.name?.trim(),
    proprietor.address?.street?.trim(),
    proprietor.address?.city?.trim(),
    proprietor.address?.province?.trim(),
    proprietor.address?.postalCode?.trim(),
  ];

  return values.some(Boolean);
}
const order = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
const DepartmentsToggle: React.FC<DepartmentsToggleProps> = ({
  selectedDepartment,
  closeDepartment,
}) => {
  const sanatizedhtml = sanitizeHtmlString(
    selectedDepartment?.manager?.proprietor?.address?.address1
  );
  console.log("selectedDepartment==>", selectedDepartment);
  const departmentHours = selectedDepartment?.hours || {};
  // Check if there is at least one non-null time for valid days
  const hasValidHours = order?.some((day) => departmentHours[day]);

  return (
    <Box className="border-solid border-[1px] shadow-cardShadow mt-5  rounded-md">
      <Box className="border-solid py-4  px-6 flex justify-between">
        <Text as="h3" className="text-xl font-sans text-brandSecondary">
          {selectedDepartment?.type}
        </Text>
        <Text
          as="button"
          aria-label="department close"
          className="icon-close text-brandBlack cursor-pointer"
          onClick={closeDepartment}
        ></Text>
      </Box>
      <Box className="px-6 py-4 pt-0">
        {selectedDepartment?.isTemporarilyClosed ? (
          <Text as="p" className="text-base text-brandBlack font-bold">
            Temporarily closed.
          </Text>
        ) : (
          <Row className="md:gap-0 gap-6">
            {(selectedDepartment?.manager?.name ||
              hasValidProprietor(selectedDepartment?.manager?.proprietor) ||
              selectedDepartment?.email ||
              selectedDepartment?.phone ||
              selectedDepartment?.fax) && (
              <Columns className="col-3 lg:w-3/12 2md:w-1/2 w-full">
                <Box as="div">
                  <Text
                    as="p"
                    className="text-base text-brandSecondary font-bold"
                  >
                    Department Info
                  </Text>
                  {selectedDepartment?.manager?.name && (
                    <Box as="div">
                      <Text as="p" className="text-body pt-4 pb-2 font-bold">
                        Manager
                      </Text>
                      <Text as="p" className="text-body font-normal">
                        {selectedDepartment?.manager?.name}
                      </Text>
                    </Box>
                  )}
                  {selectedDepartment?.manager?.proprietor?.address
                    ?.address1 && (
                    <Box as="div">
                      <Text as="p" className="text-body pt-4 pb-2 font-bold">
                        Proprietor
                      </Text>
                      <Text
                        as="p"
                        className="text-body font-normal [&_span]:flex"
                      >
                        {parse(sanatizedhtml)}
                      </Text>
                    </Box>
                  )}
                  {selectedDepartment?.email && (
                    <Box as="div">
                      <Text as="p" className="text-body pt-4 pb-2 font-bold">
                        Email
                      </Text>
                      <Text as="p" className="text-body font-normal">
                        <a
                          className="text-body font-normal"
                          href={`mailto:${selectedDepartment?.email}`}
                        >
                          {selectedDepartment?.email}
                        </a>
                      </Text>
                    </Box>
                  )}
                  {selectedDepartment?.phone && (
                    <Box>
                      <Text as="p" className="text-body pt-4 pb-2 font-bold">
                        Phone
                      </Text>
                      <Box as="div">
                        <a
                          className="text-body font-normal"
                          href={`tel:+${selectedDepartment.phone}`}
                        >
                          +{selectedDepartment.phone}
                        </a>
                      </Box>
                    </Box>
                  )}
                  {selectedDepartment?.fax && (
                    <Box as="div">
                      <Text as="p" className="text-body pt-4 pb-2 font-bold">
                        Fax
                      </Text>
                      <Text as="p" className="text-body font-normal">
                        +{selectedDepartment?.fax}
                      </Text>
                    </Box>
                  )}
                </Box>
              </Columns>
            )}

            {hasValidHours && (
              <Columns className="col-3 lg:w-3/12 2md:w-1/2 w-full">
                <Box as="div">
                  <Text
                    as="p"
                    className="text-base text-brandSecondary font-bold"
                  >
                    Department Hours
                  </Text>

                  {order.map((day) => {
                    const time = departmentHours[day]; // already lowercase

                    return (
                      <Box
                        key={day}
                        as="div"
                        className="flex flex-wrap 2lg:gap-6 lgs:gap-4 gap-6 pt-4"
                      >
                        <Text
                          as="p"
                          className="text-body w-[92px] font-normal capitalize"
                        >
                          {day} :
                        </Text>
                        <Text as="p" className="text-body font-bold">
                          {time ? changeTimeFormat(time) : "Closed"}
                        </Text>
                      </Box>
                    );
                  })}
                </Box>
              </Columns>
            )}

            {selectedDepartment?.description && (
              <Columns className="col-3 lg:pt-0 2md:pt-6 lg:w-3/12 2md:w-1/2 w-full">
                <Box as="div" className="">
                  <Box className="text-body font-normal [&_h3]:text-brandSecondary [&_h3]:font-bold [&_h3]:mb-4 [&_h3]:text-base [&_strong]:text-body [&_strong]:inline-block [&_p]:text-body [&_strong]:mb-1 [&_p]:mb-6 [&_strong:last-child]:mb-0 ">
                    {RenderHTML(selectedDepartment?.description)}
                  </Box>
                </Box>
              </Columns>
            )}
            {selectedDepartment?.latest_attachments &&
              selectedDepartment?.certificate && (
                <Columns className="col-3 lg:pt-0 2md:pt-6 lg:w-3/12 2md:w-1/2 w-full">
                  <Box
                    as="div"
                    className="flex justify-center items-start 2md:items-center"
                  >
                    <ImageWrapper
                      src={
                        process.env.NEXT_PUBLIC_AEM_PATH_URL +
                        selectedDepartment?.latest_attachments
                      }
                      className=" object-cover"
                      alt="Pharmacy Alberta Certificate"
                      width={191}
                      height={241}
                      style={{ objectFit: "cover" }}
                    />
                  </Box>
                </Columns>
              )}
          </Row>
        )}
      </Box>
    </Box>
  );
};

export default DepartmentsToggle;
